import React, {useEffect, useState} from "react";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Grid,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import {CourseService} from "../services/CourseService";
import {Link, useParams} from "react-router-dom";
import {CourseOverview} from "./CourseOverviewComponent";
import "./CoursePage.css";
import {Download, ExpandLess, ExpandMore, Filter, FilterAlt, Send} from "@mui/icons-material";
import {Header} from "../common/Header";

interface CoursesPageProps {
    courseService: CourseService
}

interface FilterState {
    open: boolean,
    year: boolean,
    courses: any
}

// Change Filter to a more complex filter option and handle the courses using types!!
interface CoursesPageState {
    courses?: any;
    coursesCalendar?: any
    loading: boolean
}


const CoursesPage = ({courseService = new CourseService()}: CoursesPageProps): any => {
    const {year} = useParams();
    const initCourses: CoursesPageState = {
        courses: [],
        coursesCalendar: [],
        loading: true
    };

    const [courses, setCourses] = useState<CoursesPageState>(initCourses);
    const [filter, setFilter] = useState<FilterState>({open: true, year: false, courses: []})
    const loadCourses = async () => {
        try {
            const json: any = await courseService.getCoursesByYear(Number(year));
            setCourses({
                courses: Object.entries(json.courses),
                coursesCalendar: Object.entries(json.courses),
                loading: false
            })

            setFilter(filter => ({
                ...filter,
                courses: json.courses.map((course: any) => (course[0].course_template.name))
            }));
        } catch (e) {
            console.log(e);
        }
    }
    useEffect(() => {
        //In development, Strict Mode intentionally mounts, unmounts, mounts again to surface side-effect bugs.
        loadCourses().catch(e => console.log(e));
    }, [])

    const renderList = () => {
        if (!courses.loading) {
            return (
                <div>
                    <Grid item xs={12}>
                        <Typography variant="h1">Kursplan {year} </Typography>
                    </Grid>
                    <CourseOverview courses={courses.courses}/>
                </div>
            )
        } else {
            return <p>Laden...</p>
        }
    }

    const handleFilterToggleClick = () => {
        setFilter(filter => ({
            ...filter,
            open: !filter.open,
        }));
    }

    const hasData =
        !courses.loading &&
        Array.isArray(courses.courses) &&
        courses.courses.length > 0;

    return (
        <Grid container maxWidth="lg" sx={{ margin: "auto" }}>
            <Header />

            <Grid item xs={12} md={12} sx={{ p: "0 1rem" }}>
                {courses.loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : hasData ? (
                    <>
                        <Button
                            variant="contained"
                            sx={{ mr: "1rem" }}
                            startIcon={<FilterAlt />}
                            endIcon={filter.open ? <ExpandLess /> : <ExpandMore />}
                            onClick={handleFilterToggleClick}>Filter</Button>

                        <Link to="/downloads/Lehrgangskatalog Lkr. Roth 2026.pdf" target="_blank" rel="noopener noreferrer">
                            <Button variant="text" endIcon={<Download />}>
                                PDF-Version herunterladen
                            </Button>
                        </Link>

                        <Collapse in={filter.open} timeout="auto" unmountOnExit sx={{ mt: "0.5rem" }}>
                            <Paper elevation={1} sx={{ p: "1rem" }}>
                                <Typography variant="subtitle1">Lehrgangsart suchen:</Typography>
                                <Autocomplete
                                    disablePortal
                                    options={filter.courses}
                                    sx={{ width: "100%" }}
                                    onChange={(_, value: string | null) => {
                                        if (!value) return;
                                        const headline = document.getElementById(value);
                                        headline?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Lehrgang" />}
                                />
                            </Paper>
                        </Collapse>

                        <CourseOverview courses={courses.courses} />
                    </>
                ) : (
                    <Typography sx={{ py: 4 }}>Nichts gefunden</Typography>
                )}
            </Grid>
        </Grid>
    );
}

export default CoursesPage;
